// frontend/src/sections/Home/Hero/HeroContact.jsx
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Badge from '../../../components/common/Badge';
import Icon from '../../../components/common/Icon';

export default function HeroContact() {
  const [isVisible, setIsVisible] = useState(false);
  const heroRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = heroRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <div
      ref={heroRef}
      className="relative z-10 flex min-h-[620px] items-center px-5 py-14 sm:px-10 sm:py-16 lg:min-h-[640px] lg:px-14 lg:py-20"
    >
      <div className="grid w-full items-center gap-12 lg:grid-cols-12 lg:gap-8">
        {/* =========================================================
            سمت چپ: محتوای اصلی
        ========================================================= */}
        <div
          className={`lg:col-span-7 xl:col-span-7 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <Badge
            variant="dark"
            className="mb-6 inline-flex border border-floodlight/25 bg-floodlight/10 px-4 py-2 text-floodlight shadow-lg shadow-black/10"
          >
            <span className="relative ml-2 flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-floodlight opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-floodlight" />
            </span>
            <Icon name="trophy" className="ml-2 h-4 w-4 text-floodlight" />
            سامانه رسمی هیئت فوتبال شهرستان پاوه
          </Badge>

          <h1 className="max-w-4xl text-balance text-4xl font-black leading-[1.18] tracking-tight text-white sm:text-5xl md:text-6xl lg:text-[4rem] xl:text-[4.6rem]">
            فوتبال شهرستان پاوه
            <span className="mt-2 block bg-gradient-to-l from-floodlight via-emerald-300 to-emerald-400 bg-clip-text text-transparent">
              یکپارچه، هوشمند و حرفه‌ای
            </span>
          </h1>

          <p className="mt-7 max-w-2xl text-sm leading-8 text-white/60 sm:text-base sm:leading-9 lg:text-lg">
            مرجع رسمی مسابقات، باشگاه‌ها، بازیکنان، اخبار و رویدادهای فوتبال
            شهرستان پاوه؛ همه اطلاعات در یک سامانه مدرن، سریع و یکپارچه.
          </p>

          {/* برچسب‌های ویژگی‌ها */}
          <div className="mt-8 flex flex-wrap gap-2.5">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-3.5 py-2 text-[11px] font-bold text-white/75 backdrop-blur-md transition-all duration-300 hover:bg-white/10 hover:text-white hover:scale-105">
              <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,.7)] animate-pulse" />
              مسابقات به‌روز
            </div>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-3.5 py-2 text-[11px] font-bold text-white/75 backdrop-blur-md transition-all duration-300 hover:bg-white/10 hover:text-white hover:scale-105">
              <span className="h-2 w-2 rounded-full bg-lime-400 shadow-[0_0_10px_rgba(34,211,238,.7)] animate-pulse" />
              باشگاه‌های رسمی
            </div>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-3.5 py-2 text-[11px] font-bold text-white/75 backdrop-blur-md transition-all duration-300 hover:bg-white/10 hover:text-white hover:scale-105">
              <span className="h-2 w-2 rounded-full bg-amber-400 shadow-[0_0_10px_rgba(251,191,36,.7)] animate-pulse" />
              اخبار و اطلاعیه‌ها
            </div>
          </div>

          {/* دکمه‌های CTA */}
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link
              to="/competitions"
              className="group inline-flex h-12 items-center gap-2 rounded-full bg-floodlight px-7 text-sm font-black text-ink shadow-lime transition-all duration-300 hover:-translate-y-1 hover:bg-floodlight-300"
            >
              مشاهده مسابقات
              <Icon
                name="arrowLeft"
                className="h-5 w-5 transition-transform duration-300 group-hover:-translate-x-1"
              />
            </Link>

            <Link
              to="/clubs"
              className="inline-flex h-12 items-center gap-2 rounded-full border border-white/20 bg-white/[0.06] px-6 text-sm font-bold text-white backdrop-blur-md transition-all duration-300 hover:-translate-y-1 hover:border-floodlight/40 hover:bg-white/10"
            >
              باشگاه‌های عضو
              <Icon name="chevronLeft" className="h-5 w-5 text-white/50" />
            </Link>

            <Link
              to="/news"
              className="group inline-flex h-12 items-center gap-2 px-4 text-sm font-bold text-white/50 transition-all duration-300 hover:text-white"
            >
              آخرین اخبار
              <Icon
                name="arrowLeft"
                className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1"
              />
            </Link>
          </div>
        </div>

        {/* =========================================================
            سمت چپ: گرافیک شناور شیشه‌ای
        ========================================================= */}
        <div
          className={`hidden lg:col-span-5 lg:block transition-all duration-1000 delay-200 ${
            isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
          }`}
        >
          <div className="relative mx-auto h-[440px] w-[440px] xl:h-[480px] xl:w-[480px]">
            {/* حلقه‌ها */}
            <div className="absolute inset-4 rounded-full border border-white/[0.07]" />
            <div className="absolute inset-12 rounded-full border border-floodlight/15" />
            <div className="absolute inset-24 rounded-full bg-floodlight/10 blur-3xl" />
            <div className="absolute inset-0 animate-[spin_40s_linear_infinite] rounded-full border border-dashed border-emerald-300/20" />

            {/* مرکز: جام روی شیشه */}
            <div className="absolute inset-0 grid place-items-center">
              <div className="animate-float relative grid h-52 w-52 place-items-center rounded-[3rem] glass-dark">
                <div className="absolute inset-3 rounded-[2.4rem] border border-white/[0.06]" />
                <span className="absolute inset-0 rounded-[3rem] animate-ring-pulse border border-floodlight/30" />
                <div className="relative grid h-32 w-32 place-items-center rounded-[2.2rem] bg-gradient-to-br from-floodlight via-emerald-300 to-emerald-500 shadow-2xl shadow-emerald-500/40">
                  <Icon name="trophy" className="h-16 w-16 text-ink drop-shadow" />
                </div>
              </div>
            </div>

            {/* کارت وضعیت */}
            <div className="animate-float-delay glass-dark absolute -right-2 top-10 rounded-3xl px-5 py-4">
              <p className="text-[10px] font-medium text-white/50">وضعیت سامانه</p>
              <div className="mt-2 flex items-center gap-2">
                <span className="h-2 w-2 animate-pulse rounded-full bg-floodlight shadow-[0_0_12px_rgba(214,255,61,.8)]" />
                <span className="text-xs font-black text-white">فعال و آنلاین</span>
              </div>
            </div>

            {/* کارت شعار */}
            <div className="animate-float glass-dark absolute -left-3 bottom-14 rounded-3xl px-5 py-4">
              <p className="text-[10px] font-medium text-white/50">فوتبال شهرستان پاوه</p>
              <p className="mt-1.5 text-sm font-black text-white">حرفه‌ای‌ تر از همیشه</p>
            </div>

            {/* کارت تاکیدی لایمی کوچک */}
            <div className="accent-card animate-float-delay absolute bottom-2 right-10 rounded-[1.4rem] px-4 py-3">
              <p className="relative z-10 text-[10px] font-bold text-ink/70">فصل جاری</p>
              <p className="relative z-10 mt-0.5 text-sm font-black">لیگ و جام‌ها</p>
            </div>

            {/* برچسب بالایی */}
            <div className="absolute left-1/2 top-[-6px] -translate-x-1/2 rounded-full border border-floodlight/20 bg-floodlight/10 px-4 py-2 text-[10px] font-black text-floodlight backdrop-blur-md transition-all duration-300 hover:scale-105 hover:bg-floodlight/20">
              ⚽ FOOTBALL Paveh
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}